// The speed field pass: the wash of the current looks 01, 04, 08 and 10,
// ONE full-world quad (R2827, ui-map-interface.md §17.3.07).
//
// The speed plane (`uSpeed`, RG16F, LINEAR) holds one texel a node of the
// flow grid of `chart/pieces/current-field.ts`: R is the speed share u at
// tide factor 1 (0 on a dry node), G the wet mask. The node of index
// (i, j) stands at the world point (i, j) × `uSfStep`, which is the texel
// centre (i + 0.5, j + 0.5).
//
//  * **The tap.** A cubic B-spline in FOUR bilinear taps (Sigg and
//    Hadwiger), as the terrain plane reads its distances: every weight of
//    the basis is positive, so each axis pair of the 4 × 4 support has one
//    sample point that carries both of its texels. The result is C², so a
//    colour band has no grid line, no diamond kink and no plateau.
//  * **The speed.** R over G is the mean u of the WET nodes round the
//    point, so the share holds its value up to a ridge, and G, the wet
//    coverage, fades the wash there.
//  * **The colour.** The cold ramp of `ramp_at`, `ramp_alpha` and the five
//    inks, mixed as `rampAt` mixes them, times the look's `field_alpha`,
//    the floor fade over `field_min` and the reading's alpha. The tide and
//    the look are uniforms, so a tide step writes no texel.
//
// The pure twin of the colour and of the tap is `layers/speedfield.ts`
// (`speedColour`, `speedTap`), which the tests read. No figure is a literal
// (prelude rule 1): the ramp loop bound is the stop count of the pack,
// written into the source; the sixths are the B-spline basis.

import { CHART } from '../../gen/presentation';
import { rgb } from '../../render/color';
import { RAMP_INKS } from '../chart/pieces/current-field';
import { PRELUDE, SHARED_UNIFORMS, type UniformSpec } from './prelude';
import { worldProgram } from '../gfx/worldquad';
import type { ProgramSource } from './program';
import { speedBodyWgsl } from './wgsl/speedfield';

const T = CHART.pieces.current_arrows;

/** The stops of the cold ramp: five, which the piece test holds. */
export const RAMP_STOPS = Math.min(T.arr.ramp_at.length, T.arr.ramp_alpha.length, RAMP_INKS.length);

/** The ramp as the pass reads it: the ink and the alpha of each stop. */
export function rampVec4(): number[] {
  const out: number[] = [];
  for (let i = 0; i < RAMP_STOPS; i++) out.push(...rgb(RAMP_INKS[i]), T.arr.ramp_alpha[i]);
  return out;
}

/** The speed field pass's own uniforms, on top of `SHARED_UNIFORMS`. */
export const SPEED_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uSfStep', type: 'float', value: T.fig.sample_px, note: 'world px between two grid nodes' },
  { name: 'uSfSize', type: 'vec2', value: [1, 1], dynamic: true, note: 'the plane in texels' },
  { name: 'uSfTide', type: 'float', value: 1, dynamic: true, note: 'the tide factor now' },
  { name: 'uSfAlpha', type: 'float', value: 0, dynamic: true, note: 'field_alpha of the look' },
  { name: 'uSfMin', type: 'float', value: 0, dynamic: true, note: 'field_min of the look, 0 for no floor' },
  { name: 'uSfBand', type: 'float', value: T.fig.field_band, note: 'the u band of the floor fade' },
  { name: 'uSfRead', type: 'float', value: T.modern.alpha, dynamic: true, note: 'the alpha of the reading' },
  { name: 'uSfAt', type: 'float[]', value: [...T.arr.ramp_at].slice(0, RAMP_STOPS), note: 'the u of each ramp stop' },
  { name: 'uSfRamp', type: 'vec4[]', value: rampVec4(), note: 'the ink and the alpha of each ramp stop' },
];

/** Every uniform the speed field program declares. */
export const SPEED_ALL_UNIFORMS: readonly UniformSpec[] = [...SHARED_UNIFORMS, ...SPEED_UNIFORMS];

const BODY = `
uniform float uSfStep;
uniform vec2  uSfSize;
uniform float uSfTide;
uniform float uSfAlpha;
uniform float uSfMin;
uniform float uSfBand;
uniform float uSfRead;
uniform float uSfAt[${RAMP_STOPS}];
uniform vec4  uSfRamp[${RAMP_STOPS}];

// layers/speedfield.ts speedTap: the plane at world point p, a cubic
// B-spline in four bilinear taps. R is the summed share, G the wet cover.
vec2 speedTap(vec2 p) {
  vec2 coord = p / uSfStep;                  // node index space
  vec2 base = floor(coord);
  vec2 f = coord - base;
  vec2 f2 = f * f, f3 = f2 * f;
  vec2 w0 = (1.0 / 6.0) * (-f3 + 3.0 * f2 - 3.0 * f + 1.0);
  vec2 w1 = (1.0 / 6.0) * (3.0 * f3 - 6.0 * f2 + 4.0);
  vec2 w2 = (1.0 / 6.0) * (-3.0 * f3 + 3.0 * f2 + 3.0 * f + 1.0);
  vec2 w3 = (1.0 / 6.0) * f3;
  vec2 s0 = w0 + w1, s1 = w2 + w3;           // the four weights sum to 1
  vec2 inv = 1.0 / uSfSize;
  vec2 t0 = (base - 0.5 + w1 / s0) * inv;    // between nodes base-1 and base
  vec2 t1 = (base + 1.5 + w3 / s1) * inv;    // between nodes base+1 and base+2
  vec2 lo = mix(texture(uSpeed, vec2(t0.x, t0.y)).rg,
                texture(uSpeed, vec2(t1.x, t0.y)).rg, s1.x);
  vec2 hi = mix(texture(uSpeed, vec2(t0.x, t1.y)).rg,
                texture(uSpeed, vec2(t1.x, t1.y)).rg, s1.x);
  return mix(lo, hi, s1.y);
}

// current-field.ts rampAt: the ink and the alpha of the two stops round u.
vec4 rampOf(float u) {
  for (int i = 1; i < ${RAMP_STOPS}; i++) {
    if (u > uSfAt[i]) continue;
    float q = (u - uSfAt[i - 1]) / max(1e-9, uSfAt[i] - uSfAt[i - 1]);
    return mix(uSfRamp[i - 1], uSfRamp[i], q);
  }
  return uSfRamp[${RAMP_STOPS - 1}];
}

// current-field.ts smooth: the Hermite step of the cards.
float sfSmooth(float e0, float e1, float v) {
  float q = clamp((v - e0) / max(1e-9, e1 - e0), 0.0, 1.0);
  return q * q * (3.0 - 2.0 * q);
}

void main() {
  vec2 s = speedTap(vWorld);
  float wet = clamp(s.y, 0.0, 1.0);
  if (wet <= 0.0) { fragColor = vec4(0.0); return; }
  float u = clamp(s.x / max(s.y, 1e-6) * abs(uSfTide), 0.0, 1.0);
  vec4 r = rampOf(u);
  float a = min(1.0, r.a * uSfAlpha);
  if (uSfMin > 0.0) a *= sfSmooth(uSfMin, uSfMin + uSfBand, u);
  a *= uSfRead * wet;
  fragColor = vec4(r.rgb * a, a);
}
`;

/** The complete speed field fragment shader, prelude included. */
export const SPEED_FRAG = PRELUDE + BODY;

/** The speed field pass, as both backends read it. */
export function speedProgram(): ProgramSource {
  return worldProgram({
    name: 'speedfield', fragment: SPEED_FRAG, raw: true, uniforms: SPEED_ALL_UNIFORMS,
    wgslBody: speedBodyWgsl(),
  });
}
